/*
 * @Author: Nora Galvin
 * @Date: 2020-08-20 15:53:24
 * @Last Modified by: Nora
 * @Last Modified time: 2020-09-17 15:13:43
 */
import type { TypeAnimation, TypeInputEmitData } from './type';
import type { AtomSetting, TypeAnimationExport } from '@/app/editor/sidebar/interactions/type';
import animationTemplate from './templates/animation-settings.html';
import DropdownControl from '../controls/dropdown';
import RangePickerControl from '../controls/range-picker';
import CheckBoxControl from '../controls/checkbox';
import { camelize, showNotify } from '@/app/utils/helpers';
import animationStyle from '@/app/editor/sidebar/interactions/configs/animation-style';
import type { TypeOption } from '@/app/editor/sidebar/interactions/animations/type';
import { allAtomsIntegration } from '@/app/editor/sidebar/interactions/configs/atom.ts';

/**
 * Animation class
 */
export default class Animation {
  readonly PREFIX = 'gf__';

  _$gfLeftSideBar: JQuery;
  _$interactionTab: JQuery;
  _$interactionPanel: JQuery;
  _$interaction: JQuery;
  _$atom: JQuery;
  _$gfResTool: JQuery;

  _animation: TypeAnimation;
  _options: any;
  _data: TypeAnimationExport = {
    animation: {
      screens: {
        lg: true,
        md: true,
        sm: true,
        xs: true,
      },
    },
    interaction: {},
  };

  /**
   * Constructor
   * @param animation ..
   * @param $interaction ..
   * @param $atom ..
   * @param options ..
   */
  constructor(animation: TypeAnimation, $interaction?: JQuery, $atom?: JQuery, options?: any) {
    this._animation = JSON.parse(JSON.stringify(animation));
    if ($interaction) {
      this._$interaction = $interaction;
    }
    if ($atom) {
      this._$atom = $atom;
    }
    this._options = options;
    this._$gfLeftSideBar = jQuery('#gfLeftSideBar');
    this._$interactionTab = jQuery('.simple-tabs');
    this._$interactionPanel = this._$interactionTab.find('.interactions-panel');
    this._$gfResTool = jQuery('#gfResTool');

    this.renderTemplate();
  }

  /**
   * Getter _animation
   * @returns TypeAnimation
   */
  getAnimation(): TypeAnimation {
    return this._animation;
  }

  /**
   * Setter _animation
   * @param anim ...
   */
  setAnimation(anim: TypeAnimation): void {
    this._animation = JSON.parse(JSON.stringify(anim));
    this.renderTemplate();
    this.emitChangeAnimation(this.getValue());
  }

  /**
   * Render setting to template
   * @returns void
   */
  renderTemplate(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    const animationHTML = animationTemplate(this._animation);
    const $animationDOM = $(animationHTML);

    $animationDOM.find('select').each(function () {
      // Listen onChange
      const opts = {
        // {key: "right-in", value: "Right In", inputType: "settings", type: "direction"}
        /**
         * onChange
         * @param data ..
         */
        onChange: (data: TypeInputEmitData) => {
          _this.onChangeSettings(data);

          _this.emitChangeAnimation(_this.getValue());
          _this.runAnimate();
        },
        /**
         * onOpened
         */
        onOpened: () => {
          _this._$interactionPanel.find('.interactions-overlay').fadeIn();
        },
        /**
         * onClosed
         */
        onClosed: () => {
          _this._$interactionPanel.find('.interactions-overlay').fadeOut();
        },
      };
      new DropdownControl($(this), opts);
    });

    $animationDOM.find('input[data-type=slider]').each(function () {
      // Listen onChange
      const opts = {
        /**
         * onChange
         * @param data ..
         */
        onChange: (data: TypeInputEmitData) => {
          _this.onChangeSettings(data);

          _this.emitChangeAnimation(_this.getValue());
        },
      };
      new RangePickerControl($(this), opts);
    });

    $animationDOM.find('input[data-type=checkbox]').each(function () {
      // Listen onChange
      const opts = {
        /**
         * onChange
         * @param data ..
         */
        onChange: (data: TypeInputEmitData) => {
          _this.onChangeSettings(data);

          _this.emitChangeAnimation(_this.getValue());
        },
      };
      new CheckBoxControl($(this), opts);
    });

    $animationDOM
      .find('.view-animation')
      .off('click')
      .on('click', async function () {
        const screenType =
          _this._$gfResTool.data('gfrestool') != undefined
            ? _this._$gfResTool.data('gfrestool').getCurrentStatus()
            : 'lg';
        const screenOptions = _this.getScreenOptions();

        if (screenOptions) {
          const screen = screenOptions.options?.find((e) => e.key == screenType);
          if (screen && screen.value == true) {
            $(this).find('svg').eq(0).hide();
            $(this).find('svg').eq(1).show();

            await _this.runAnimate();

            $(this).find('svg').eq(0).show();
            $(this).find('svg').eq(1).hide();
          } else {
            showNotify('You have disabled this animation on current screen', 'error');
          }
        }
      });

    this._$interaction.find('.interaction-content').append($animationDOM);

    // Emit data to update bkContent
    this.emitChangeAnimation(this.getValue());
    return;
  }

  /**
   * Get Screen Options
   * @returns TypeOption
   */
  getScreenOptions(): TypeOption | undefined {
    return this._animation.options?.find((e) => e.key == 'screens');
  }

  /**
   * getValue
   * @returns any
   */
  getValue(): TypeAnimationExport {
    let suffix = '';
    this._animation.settings?.forEach((e) => {
      if (suffix) {
        suffix += `-${e.value}`;
      } else {
        suffix += e.value;
      }
    });

    if (this._animation) {
      this._data.animation.name = this._animation.key;
    }

    if (this._data && this._data.animation && this._data.animation.name && suffix != 'none') {
      if (suffix && this._data.animation.name.includes('_')) {
        this._data.animation.name = this._data.animation.name.replace(/_[a-zA-Z-]*$/, `_${suffix}`);
      } else {
        if (suffix) {
          this._data.animation.name += `_${suffix}`;
        }
      }
    }

    if (suffix == 'none') {
      if (this._data && this._data.animation && this._data.animation.name) {
        this._data.animation.name = this._data.animation.name.replace(/_[a-zA-Z-]*$/, '');
      }
    }

    this._animation.options?.forEach((e) => {
      switch (e.key) {
        case 'screens':
          e.options?.forEach((eOp) => {
            (this._data.animation.screens as any)[eOp.key] = eOp.value;
          });
          break;
        case 'loop':
          e.options?.forEach((eOp) => {
            if (typeof eOp.value == 'boolean' && eOp.value == true) {
              this._data.animation['iteration-count'] = 'infinite';
            } else {
              // TODO: config number of iteration here
              this._data.animation['iteration-count'] = '1';
            }
          });
          break;
        default:
          (this._data.animation as any)[e.key] = e.value;
          break;
      }
    });

    // mouse-click_level2_e-1597636699687
    const interactionData = this._$interaction.data('interaction');
    this._data.interaction.key = interactionData.split('_')[0];
    this._data.interaction.type = interactionData.split('_')[1];

    return this._data;
  }

  /**
   * // Emit Animation to Interaction
   * @param data ..
   * @returns void
   */
  emitChangeAnimation(data: any): void {
    if (this._options && this._options.onChange) {
      this._options.onChange(data);
    }
  }

  /**
   * onChangeSettings
   * @param data ..
   * @returns void
   */
  onChangeSettings(data: TypeInputEmitData): void {
    this.updateSettings(data);
  }

  /**
   * Update settings
   * @param data ..
   * @returns void
   */
  updateSettings(data: TypeInputEmitData): void {
    if (!data) return;

    if (data.inputType == 'settings') {
      if (this._animation.settings) {
        for (let i = 0; i < this._animation.settings?.length; i++) {
          if (this._animation.settings[i].key == data.type) {
            this._animation.settings[i].value = data.key;
          }
        }
      }
    } else {
      if (!this._animation.options) this._animation.options = [];

      for (let i = 0; i < this._animation.options.length; i++) {
        if (this._animation.options[i].key == data.type) {
          if (data.type == 'screens' || data.type == 'loop') {
            if (!this._animation.options[i].options) this._animation.options[i].options = [];
            const settingsOptions = this._animation.options[i].options || [];
            for (let j = 0; j < settingsOptions.length; j++) {
              if ((settingsOptions as any)[j].key == data.key) {
                settingsOptions[j].value = data.value;
              }
            }
          } else {
            this._animation.options[i].value = data.value;
          }
        }
      }
    }

    return;
  }

  /**
   * FindAtomSetting
   * @param label ..
   * @returns AtomSetting | undefined
   */
  findAtomSetting(label: string): AtomSetting | undefined {
    return allAtomsIntegration.get(label);
  }

  /**
   * Run animate
   * @returns promise
   */
  runAnimate(): Promise<string> {
    // const _this = this;
    const animData = this.getValue();
    let animation = camelize(animData.animation.name?.replace('_', '-') || '');

    // Fix trường hợp 1 số animation tên trùng nhau như: (E) Bounce và (S) Bounce
    // Khi đó key 2 cái sẽ là bounce và bounce_
    // Dòng trên chuyển về camel case và replace _ thành - nên chỉ cần trim đi là được
    if (animation.includes('-')) {
      animation = animation.replace('-', '');
    }

    // Target Atom Animated
    const atomSetting = this.findAtomSetting(this._$atom.data('label'));
    let $target = this._$atom.children();
    if (atomSetting) {
      if (atomSetting.target) {
        $target = this._$atom.find(atomSetting.target).first();
      } else {
        $target = this._$atom;
      }
    }

    // Clear all gf__animated and gf__animateName
    const gfAnimateClasses = $target.attr('class');
    const matches = gfAnimateClasses?.match(/gf__[a-zA-Z-_]*/g);
    if (matches && matches.length) {
      $target.removeClass(matches.join(' '));
    }

    // We create a Promise and return it
    return new Promise<string>((resolve, _reject) => {
      const animationName = `${this.PREFIX}${animation}`;
      const addedClass = [`${this.PREFIX}animated`];
      $target.addClass(addedClass.join(' '));

      const animStyles = (animationStyle as any)[animationName];
      if (animData.animation.delay != undefined) {
        const delay = animData.animation.delay;
        animStyles['animation-delay'] = `${+delay / 1000}s`;
        animStyles['-webkit-animation-delay;'] = `${+delay / 1000}s`;
      }

      if (animData.animation.duration != undefined) {
        let duration = animData.animation.duration;
        switch (animationName) {
          case 'gf__heartbeat':
            duration = +duration * 1.3;
            break;
          case 'gf__bounceIn':
          case 'gf__bounceOut':
          case 'gf__flipOutX':
          case 'gf__flipOutY':
            duration = +duration * 0.75;
            break;
        }
        animStyles['animation-duration'] = `${+duration / 1000}s`;
        animStyles['-webkit-animation-duration'] = `${+duration / 1000}s`;
      }
      for (const key of Object.keys(animStyles)) {
        $target.css(key, animStyles[key]);
      }

      /**
       * When the animation ends, we clean the classes and resolve the Promise
       */
      function handleAnimationEnd() {
        $target.removeClass(addedClass.join(' '));

        for (const key of Object.keys(animStyles)) {
          $target.css(key, '');
        }

        $target.off('animationend', handleAnimationEnd);

        resolve('Animation ended');
      }

      // Check screens then run animate
      $target.on('animationend', handleAnimationEnd);
    });
  }

  /**
   * Get contentbk data
   * @returns export
   */
  getContentBkData(): TypeAnimationExport {
    return this.getValue();
  }
}
